import { type ErrorComponentProps, useRouter } from "@tanstack/react-router"
import { ApiError, isNetworkError } from "@/lib/api"
import { reportError } from "@/lib/telemetry"
import { LogoMark } from "./logo"
import { Button } from "./ui/button"

/**
 * A part of the app that failed to load because the page still runs a previous deploy (deploy skew, decisions 0052
 * and TRI-09): the browsers' dynamic-import failures, a bundler's `ChunkLoadError`, and, defensively, the "reading
 * 'default'" crash of an import that resolved to nothing.
 */
export const isChunkLoadError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false
  if (error.name === "ChunkLoadError") return true
  const m = error.message
  return (
    m.includes("Failed to fetch dynamically imported module") ||
    m.includes("Importing a module script failed") ||
    m.includes("error loading dynamically imported module") ||
    m.includes("reading 'default'")
  )
}

const reported = new WeakSet<object>()
/** A boundary renders more than once per error; it is reported the first time only. */
const reportOnce = (error: unknown): void => {
  if (typeof error === "object" && error !== null) {
    if (reported.has(error)) return
    reported.add(error)
  }
  reportError(error, "boundary")
}

/**
 * Root error boundary: a loader failed (e.g. the API is unreachable while loading the session) or a
 * page crashed while rendering. Only the first case is "couldn't reach Minga". A part of the app that failed to load
 * after a deploy needs the page reloaded (re-rendering keeps the failed import), so it gets Reload instead of Try again:
 * a person's click, never limited like the automatic reload in index.html.
 */
export function RouteError({ error }: ErrorComponentProps) {
  const router = useRouter()
  // decision 0143: what a person saw fail, reported once per error (name, route, top frames; no content)
  if (!(error instanceof ApiError) && !isNetworkError(error)) reportOnce(error)
  if (isChunkLoadError(error))
    return (
      <main className="flex min-h-dvh flex-col items-center justify-center gap-4 px-4 text-center">
        <LogoMark />
        <h1 className="type-card">Minga was updated</h1>
        <p className="max-w-sm text-sm text-muted">Reload to get the latest version.</p>
        <Button variant="secondary" onClick={() => globalThis.location.reload()}>
          Reload
        </Button>
      </main>
    )
  const unreachable = error instanceof ApiError || isNetworkError(error)
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-4 px-4 text-center">
      <LogoMark />
      <h1 className="type-card">{unreachable ? "Couldn't reach Minga" : "Something went wrong"}</h1>
      <p className="max-w-sm text-sm text-muted">
        {error instanceof Error && error.message ? error.message : "Something went wrong."}
      </p>
      <Button variant="secondary" onClick={() => router.invalidate()}>
        Try again
      </Button>
    </main>
  )
}
